import { startAuthentication } from "@simplewebauthn/browser";
import { IconFingerprint, IconMessage2Code } from "@tabler/icons-react";
import { useState, useSyncExternalStore } from "react";
import { Navigate, useSearchParams } from "react-router";
import { useT } from "../i18n/provider";
import { Button, Card, Field, Input } from "../components/kit";
import { useSession } from "../lib/session";

/**
 * Sign-in — ADR-0013 §2: **ADMIN and FINANCE sessions must be opened with a
 * passkey**, and the api refuses every handler on a session that was not, except
 * the enrolment endpoints.
 *
 * So the passkey is the front door and the SMS code is the side door that leads
 * only to enrolment. Saying that out loud on the screen is the point: an operator
 * who signs in with a code and then finds every register refusing them has been
 * misled by a form that offered two equal-looking choices.
 *
 * The token never reaches this page. `POST /api/auth/passkey/login/verify` is
 * answered by the console's own server, which turns the api's `sessionDto` into
 * an httpOnly cookie and strips the token from the body.
 */
export function SignInPage() {
  const { i18n } = useT();
  const { state, refresh } = useSession();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get("returnTo"));

  if (state.status === "ready") return <Navigate to={returnTo} replace />;

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <header className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-foreground">{i18n._("Shoppy staff console")}</h1>
          <p className="text-sm text-muted-foreground">
            {i18n._("Sign in with the passkey registered to your device.")}
          </p>
        </header>
        <PasskeyForm onSignedIn={refresh} />
        <OtpForm onSignedIn={refresh} />
      </div>
    </div>
  );
}

/**
 * `returnTo` is used as a redirect target, so it may only ever be a path on this
 * origin. An absolute URL or a protocol-relative `//host` would make the sign-in
 * page an open redirect, which is a phishing primitive on a staff console.
 */
function safeReturnTo(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

function usePasskeySupport(): boolean {
  // `useSyncExternalStore` rather than an effect: the answer never changes for
  // the life of the page, and reading it during render with no subscription is
  // what the hook is for.
  return useSyncExternalStore(
    () => () => {},
    () => typeof window !== "undefined" && window.PublicKeyCredential !== undefined,
    () => false,
  );
}

function PasskeyForm({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const { i18n } = useT();
  const supported = usePasskeySupport();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      // Discoverable credentials: no phone is typed, so an operator's number is
      // not entered on a shared machine before they are authenticated.
      const options = await post("passkey/login/options", {});
      if (!options.ok) throw new Error(options.detail);
      const assertion = await startAuthentication({ optionsJSON: options.body as never });
      const verified = await post("passkey/login/verify", { response: assertion });
      if (!verified.ok) throw new Error(verified.detail);
      await onSignedIn();
    } catch (cause) {
      setError(cause instanceof Error && cause.message ? cause.message : i18n._("That passkey was not accepted."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={i18n._("Passkey")}>
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">
          {i18n._("This is the only way into the console itself. A code signs you in far enough to register a passkey and no further.")}
        </p>
        <Button
          variant="primary"
          icon={IconFingerprint}
          busy={busy}
          disabled={!supported}
          onClick={() => void signIn()}
          data-autofocus
        >
          {i18n._("Sign in with a passkey")}
        </Button>
        {!supported && (
          <p className="text-xs text-caution">{i18n._("This browser does not offer passkeys.")}</p>
        )}
        {error && <p className="text-xs text-critical">{error}</p>}
      </div>
    </Card>
  );
}

function OtpForm({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const { i18n } = useT();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = async () => {
    setBusy(true);
    setError(null);
    const result = await post("otp/request", { phone, purpose: "LOGIN" });
    setBusy(false);
    if (!result.ok) return setError(result.detail);
    setSent(true);
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    const result = await post("otp/verify", { phone, purpose: "LOGIN", code });
    setBusy(false);
    if (!result.ok) return setError(result.detail);
    await onSignedIn();
  };

  return (
    <Card title={i18n._("Code by SMS")}>
      <div className="flex flex-col gap-3">
        <Field label={i18n._("Phone")} hint={i18n._("The number on your staff account.")} htmlFor="phone">
          <Input
            id="phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+976"
          />
        </Field>
        {sent && (
          <Field label={i18n._("Code")} htmlFor="code">
            <Input
              id="code"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
        )}
        <Button
          icon={IconMessage2Code}
          busy={busy}
          onClick={() => void (sent ? verify() : request())}
        >
          {sent ? i18n._("Sign in") : i18n._("Send a code")}
        </Button>
        {error && <p className="text-xs text-critical">{error}</p>}
      </div>
    </Card>
  );
}

interface AuthAnswer {
  ok: boolean;
  detail: string;
  body: unknown;
}

async function post(path: string, body: unknown): Promise<AuthAnswer> {
  const response = await fetch(`/api/auth/${path}`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-requested-by": "shoppy-admin" },
    body: JSON.stringify(body),
    credentials: "same-origin",
  }).catch(() => null);

  if (!response) return { ok: false, detail: "", body: null };
  const text = await response.text();
  // A body that is not JSON is a proxy or a gateway answering, not the api.
  const parsed: unknown = safeJson(text);
  const problem = (parsed ?? {}) as { detail?: string; code?: string };
  return {
    ok: response.ok,
    detail: problem.detail ?? problem.code ?? "",
    body: parsed,
  };
}

function safeJson(text: string): unknown {
  if (text === "") return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}
